import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, List, ListItem } from "@mui/material";
import { FileOrLink, IAttachmentFormPayload, IAttachmentTypeListItem, IAttachmentValidator } from "models/attachments";
import React, { useEffect, useState } from "react";
import AttachmentFormatSelection from "./AttachmentFormatSelection";
import AttachmentLinkForm from "./AttachmentLinkForm";
import AttachmentFileForm from "./AttachmentFileForm";
import { Ico_ExclamationMark } from "@sagecentral/core-ui";
import AttachmentTypeList from "./AttachmentTypeList";

type AttachmentFormProps = {
    edit?: boolean;
    processed?: boolean;
    saving: boolean;
    payload: IAttachmentFormPayload;
    updatePayload: React.Dispatch<Partial<IAttachmentFormPayload>>;
    validator: IAttachmentValidator;
    types: IAttachmentTypeListItem[] | null;
    onSave: () => Promise<void>;
    onClose: () => void;
    onCheckExisting: (payload: IAttachmentFormPayload) => boolean;
    draft?: boolean;
};

const AttachmentForm: React.FC<AttachmentFormProps> = (props) => {
    const {
        edit,
        processed,
        saving,
        types,
        payload,
        updatePayload,
        validator,
        onClose,
        onSave,
        onCheckExisting,
        draft
    } = props;
    const islinkRequired = payload.typeId !== 0 && types?.find(type => type.id == payload.typeId)?.linkRequired && !types?.find(type => type.id == payload.typeId)?.fileRequired;

    const [ showVersionConfirm, setShowVersionConfirm ] = useState<boolean>(false);
    const [ saveWorking, setSaveWorking ] = useState<boolean>(false);

    useEffect(() => {
        if(islinkRequired == true){
            validator.reset();
            updatePayload({
                files: null,
                name: '',
                url: '',
                fileOrLink: FileOrLink.Link
            });
        }
    }, [islinkRequired])

    const checkExisting = () => {
        if (payload.fileOrLink === FileOrLink.Link) 
            return false;

        if (onCheckExisting(payload)) {
            setShowVersionConfirm(true);
            return true;
        }

        return false;
    }

    const handleSaveClick = async () => {
        setSaveWorking(true);
        if (edit || !checkExisting()) {
            await onSave();
        }
        setSaveWorking(false);
    };

    const handleConfirm = async () => {
        setSaveWorking(true);
        setShowVersionConfirm(!showVersionConfirm);
        await onSave();
        setSaveWorking(false);
    };

    const handleFileOrLinkChanged = (value: FileOrLink) => {
        validator.reset();
        updatePayload({
            files: null,
            name: '',
            url: '',
            fileOrLink: value
        });
    };

    const handleTypeChange = (value: number) => {
        validator.reset();
        updatePayload({
            typeId: value
        });
    };

    const canSave = !validator.state.working && validator.isValid;
   

    return (
        <>
            <List>
                <ListItem>
                    <AttachmentTypeList
                        types={types}
                        value={payload.typeId}
                        onChanged={handleTypeChange}
                        disabled={edit}                        
                    />
                </ListItem>
                <ListItem>
                    <AttachmentFormatSelection 
                        disabled={payload.typeId === 0 || edit}
                        value={payload.fileOrLink}
                        onChanged={handleFileOrLinkChanged}
                        islinkRequired={islinkRequired}
                    />
                </ListItem>
                {(payload.fileOrLink != FileOrLink.File) &&
                    <AttachmentLinkForm
                        payload={payload}
                        updatePayload={updatePayload}
                        validator={validator}
                        disabled={processed}
                    />
                }
                {payload.fileOrLink == FileOrLink.File &&
                    <AttachmentFileForm
                        payload={payload}
                        updatePayload={updatePayload}
                        validator={validator}
                    />
                }
                <ListItem sx={{ px: '8px' }}>
                    <Button fullWidth={true} variant="outlined" onClick={onClose}>Cancel</Button>
                    <Button fullWidth={true} variant='contained' 
                        disabled={!canSave || saveWorking} 
                        onClick={handleSaveClick}
                        endIcon={ saving ? <CircularProgress 
                            size={20}
                            sx={{                                
                                position: 'unset',
                                color: (theme) => theme.palette.primary.contrastText
                            }}
                            
                        /> : null}
                    >
                        Save
                    </Button>
                </ListItem>
            </List>

            {/* Duplication Confirmation Pop-up */}
            <Dialog fullWidth={false} maxWidth={'lg'} onClose={setShowVersionConfirm} open={showVersionConfirm}>
                <DialogTitle sx={{textAlign: 'left', fontWeight:'600', fontSize:'17px'}}>
                    {/* {<Ico_Warning color='error' sx={{ height: '40px', width: '40px' }} />}  */}
                    <Ico_ExclamationMark sx={{ height: '40px', width: '40px', fill: '#001A69' }} />
                </DialogTitle>
                <DialogContent>
                    {draft ? "The file you have uploaded is a duplicate. Would you like to continue?" : "The file you have uploaded is a duplicate and will be versioned. Would you like to continue?"}
                    <DialogActions sx={{ paddingTop: '20px', width:'100%' }}>
                        <Button fullWidth={true} disabled={saveWorking} variant='contained' onClick={handleConfirm}>Yes</Button>
                        <Button fullWidth variant="outlined" onClick={() => {
                            setShowVersionConfirm(!showVersionConfirm);
                        }}>
                            Cancel
                        </Button>
                    </DialogActions>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default AttachmentForm;